"use client";
/* eslint-disable no-unused-vars */

import React, { useMemo } from "react";

import { generateEventHandlers } from "@stadium/ui/src/components/form/TextInput/helpers";

import { EventArgs } from "@stadium/ui/types/form-text-input";

import defaultStyles from "./main.module.scss";

const defaultValidate = (e: EventArgs) => e;

export interface TextInputProps
  extends Omit<React.ComponentPropsWithoutRef<"input">, "onChange" | "prefix"> {
  validate?: (options: EventArgs) => EventArgs;
  onChange: (options: EventArgs) => EventArgs | unknown;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  /**
   * Make sure to pass only Label instance from ui/src/components/form/Label
   */
  label?: React.ReactNode;
  /**
   * These classes are appended to the parent container
   */
  classes?: string;
  /**
   * If the form field has error, pass this as true
   */
  error?: boolean;
  styles?: ModularStyles;
  inputContainerClass?: string;
}

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      onChange = null,
      onFocus: propsOnFocus = null,
      onBlur: propsOnBlur = null,
      onClick = null,
      onPaste = null,
      validate = defaultValidate,
      autoComplete = "on",
      label = null,
      placeholder = "",
      classes = "",
      id = `input-${Date.now()}`,
      autoFocus = false,
      name = "text-input",
      value = "",
      type = "text",
      disabled = false,
      error = false,
      readOnly = false,
      tabIndex = 0,
      onKeyPress = null,
      prefix = null,
      suffix = null,
      inputMode = "text",
      className = "",
      inputContainerClass = "",
      step,
      pattern,
      styles = defaultStyles,
      min,
      max,
      ...rest
    },
    ref,
  ) => {
    const { onFocus, onBlur } = useMemo(
      () => generateEventHandlers(propsOnFocus, propsOnBlur),
      [propsOnFocus, propsOnBlur],
    );

    return (
      <div className={`${styles.formInput} ${classes} ${error ? styles.formInputError : ""}`}>
        {React.isValidElement(label) &&
          React.cloneElement(label, { disabled, htmlFor: id, "aria-disabled": disabled })}
        <span className={inputContainerClass}>
          {prefix}
          <input
            min={min}
            max={max}
            type={type}
            placeholder={placeholder}
            name={name}
            id={id}
            className={`${styles.inputClass} ${className}`}
            ref={ref}
            disabled={disabled}
            readOnly={readOnly}
            autoFocus={autoFocus}
            value={value}
            tabIndex={tabIndex}
            autoComplete={autoComplete}
            onChange={(e) =>
              onChange(validate({ name: e.target.name, value: e.target.value, _e: e }))
            }
            step={step}
            onClick={onClick}
            onFocus={onFocus}
            onBlur={onBlur}
            onPaste={onPaste}
            onKeyPress={onKeyPress}
            inputMode={inputMode}
            pattern={pattern}
            {...rest}
          />
          {suffix}
          {label && <label htmlFor={id} className={styles.animatedLine} />}
        </span>
      </div>
    );
  },
);

export default TextInput;
